import { BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { BillingApi } from '@/features/billing/api/billing-api'
import Env from '@/infra/env'
import { BillingMethodListViewModel } from '@/features/billing/profile/BillingMethodList/BillingMethodListViewModel'
import { CardBillingMethodEditorViewModel } from '@/features/billing/profile/CardBillingMethodEditor/CardBillingMethodEditorViewModel'
import { TransactionStore } from '@/features/billing/transactions/TransactionStore'
import { TransactionsTableViewModel } from '@/features/billing/transactions/TransactionsTable/TransactionsTableViewModel'
import {
  type AddCouponFn,
  type ApplePayViewModelFactory,
  type BillingFeatureOptions,
  type BillingMethodListViewModelFactory,
  type CardBillingMethodEditorViewModelFactory,
  type ChangeDefaultBillingMethodFn,
  type CheckHasBillingMethodFn,
  type FetchCouponFn,
  type ListCouponsFn,
  type NewBillingMethodDialogViewModelFactory,
  type TransactionsTableViewModelFactory,
} from '@/features/billing'
import { NewBillingMethodDialogViewModel } from './profile/NewBillingMethodDialog/NewBillingMethodDialogViewModel'
import { CouponStore } from '@/features/billing/coupons/CouponStore'
import { ApplePayViewModel } from '@/features/billing/profile/ApplePayViewModel'
import { ExternalCheckoutStore } from './external-checkout/ExternalCheckoutStore'

export function InternalBillingFeature(options: BillingFeatureOptions) {
  const api = new BillingApi({
    token$: options.token$,
    baseURL: Env.BILLING_API,
  })

  const billingProfileStore = new BillingProfileStore(api)
  const transactionStore = new TransactionStore(api)
  const couponStore = new CouponStore(api, options.getCurrentMember)
  const externalCheckoutStore = new ExternalCheckoutStore(api.stripe)

  const createBillingMethodListViewModel: BillingMethodListViewModelFactory =
    () =>
      new BillingMethodListViewModel(
        options.getCurrentMember,
        options.newSnackbar,
        options.createSnackbarRetryWrapper,
        billingProfileStore,
      )

  const createCardBillingMethodEditorViewModel: CardBillingMethodEditorViewModelFactory =
    () =>
      new CardBillingMethodEditorViewModel(
        options.getCurrentMember(),
        options.newSnackbar,
        billingProfileStore,
      )

  const createNewBillingMethodDialogViewModel: NewBillingMethodDialogViewModelFactory =
    () =>
      new NewBillingMethodDialogViewModel(
        () =>
          new CardBillingMethodEditorViewModel(
            options.getCurrentMember(),
            options.newSnackbar,
            billingProfileStore,
          ),
      )

  const createTransactionsTableViewModel: TransactionsTableViewModelFactory = (
    scope,
  ) =>
    new TransactionsTableViewModel(
      options.createSnackbarRetryWrapper,
      transactionStore,
      billingProfileStore,
      options.getCurrentMember,
      scope,
    )

  const changeDefaultBillingMethod: ChangeDefaultBillingMethodFn = (method) =>
    billingProfileStore.changeDefaultMethod(method)

  const checkHasDefaultBillingMethod: CheckHasBillingMethodFn = (member) =>
    billingProfileStore.checkHasBillingMethod(member)

  const listCoupons: ListCouponsFn = (signal) => couponStore.listCoupons(signal)
  const addCoupon: AddCouponFn = (code: string) => couponStore.addCoupon(code)
  const fetchCoupon: FetchCouponFn = (id: string, signal?: AbortSignal) =>
    couponStore.fetchCoupon(id, signal)

  const createApplePayViewModel: ApplePayViewModelFactory = (amount$, submit) =>
    new ApplePayViewModel({
      member: options.getCurrentMember(),
      billingProfileStore,
      amount$,
      submit,
    })

  return {
    // Internal
    billingProfileStore,
    transactionStore,
    couponStore,
    externalCheckoutStore,
    createBillingMethodListViewModel,
    changeDefaultBillingMethod,
    createCardBillingMethodEditorViewModel,
    createTransactionsTableViewModel,
    createNewBillingMethodDialogViewModel,
    createApplePayViewModel,
    checkHasDefaultBillingMethod,
    listCoupons,
    addCoupon,
    fetchCoupon,
  }
}
